<script setup>
import { reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['save']);

const { t } = useI18n();

const businessInfo = useMapGetter('metaAgent/getBusinessInfo');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

// Meta replaces the record wholesale, so the form mirrors every field it owns
// and always submits all of them.
const form = reactive({
  business_description: '',
  purchase_info: '',
  payment_method: '',
  delivery_and_shipping: '',
  return_policy: '',
  email: '',
  hours_of_operation: '',
  address: '',
});

const syncForm = () => {
  const remote = businessInfo.value || {};
  const contact = remote.contact_info || {};
  form.business_description = remote.business_description || '';
  form.purchase_info = remote.purchase_info || '';
  form.payment_method = remote.payment_method || '';
  form.delivery_and_shipping = remote.delivery_and_shipping || '';
  form.return_policy = remote.return_policy || '';
  form.email = contact.email || '';
  form.hours_of_operation = contact.hours_of_operation || '';
  form.address = contact.address || '';
};

const save = () => {
  emit('save', {
    business_description: form.business_description,
    purchase_info: form.purchase_info,
    payment_method: form.payment_method,
    delivery_and_shipping: form.delivery_and_shipping,
    return_policy: form.return_policy,
    contact_info: {
      email: form.email,
      hours_of_operation: form.hours_of_operation,
      address: form.address,
    },
  });
};

watch(businessInfo, syncForm, { immediate: true, deep: true });
</script>

<template>
  <div class="flex flex-col gap-4">
    <span class="text-sm text-n-slate-11">
      {{ t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.DESCRIPTION') }}
    </span>

    <TextArea
      v-model="form.business_description"
      :label="t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.BUSINESS_DESCRIPTION')"
      :max-length="2000"
      auto-height
    />
    <TextArea
      v-model="form.purchase_info"
      :label="t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.PURCHASE_INFO')"
      :max-length="2000"
      auto-height
    />
    <TextArea
      v-model="form.payment_method"
      :label="t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.PAYMENT_METHOD')"
      :max-length="2000"
      auto-height
    />
    <TextArea
      v-model="form.delivery_and_shipping"
      :label="t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.DELIVERY_AND_SHIPPING')"
      :max-length="2000"
      auto-height
    />
    <TextArea
      v-model="form.return_policy"
      :label="t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.RETURN_POLICY')"
      :max-length="2000"
      auto-height
    />

    <div
      class="flex flex-col gap-3 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.CONTACT') }}
      </span>
      <Input
        v-model="form.email"
        :label="t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.EMAIL')"
      />
      <Input
        v-model="form.hours_of_operation"
        :label="t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.HOURS_OF_OPERATION')"
      />
      <Input
        v-model="form.address"
        :label="t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.ADDRESS')"
      />
    </div>

    <div class="flex justify-end">
      <Button
        :label="t('META_AGENT.KNOWLEDGE.BUSINESS_INFO.SAVE')"
        size="sm"
        :is-loading="uiFlags.savingKnowledge"
        @click="save"
      />
    </div>
  </div>
</template>
